// src/pages/Support.js
import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { supportAdminService } from '../services/supportAdminService';
import './Support.css';

const Support = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    resolved: 0,
    closed: 0
  });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await supportAdminService.getSupportRequests();
      setRequests(data || []);
      
      const statsData = {
        total: data.length,
        pending: data.filter(r => r.status === 'pending').length,
        processing: data.filter(r => r.status === 'processing').length,
        resolved: data.filter(r => r.status === 'resolved').length,
        closed: data.filter(r => r.status === 'closed').length
      };
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await supportAdminService.updateSupportStatus(requestId, newStatus);
      await fetchRequests();
      alert(`✅ Statut mis à jour: ${getStatusLabel(newStatus)}`);
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;
    try {
      await supportAdminService.deleteSupportRequest(requestId);
      await fetchRequests();
      alert('✅ Demande supprimée !');
      if (selectedRequest?._id === requestId) setSelectedRequest(null);
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', icon: '⏳', color: '#f59e0b', bg: '#fef3c7' },
      processing: { label: 'En traitement', icon: '🔄', color: '#3b82f6', bg: '#dbeafe' },
      resolved: { label: 'Résolu', icon: '✅', color: '#10b981', bg: '#d1fae5' },
      closed: { label: 'Fermé', icon: '🔒', color: '#6b7280', bg: '#f3f4f6' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`status-badge ${status}`} style={{ backgroundColor: config.bg, color: config.color }}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      help_request: { label: 'Aide', icon: '❓', color: '#8b5cf6' },
      problem_report: { label: 'Problème', icon: '🐛', color: '#ef4444' },
      bug: { label: 'Bug', icon: '🐞', color: '#f97316' },
      question: { label: 'Question', icon: '💬', color: '#06b6d4' }
    };
    const config = typeConfig[type] || typeConfig.help_request;
    return (
      <span className="type-badge" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En traitement',
      resolved: 'Résolu',
      closed: 'Fermé'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(request => {
    if (statusFilter !== 'all' && request.status !== statusFilter) return false;
    if (typeFilter !== 'all' && request.type !== typeFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="support">
        <div className="page-header">
          <h1>Gestion du Support</h1>
          <p>Chargement...</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="support">
      <div className="page-header">
        <div className="page-title">
          <h1>🛟 Gestion du Support</h1>
          <p>Gérez les demandes d'aide ({requests.length} demande{requests.length > 1 ? 's' : ''})</p>
        </div>
      </div>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">❌ {error}</div>
        </Card>
      )}

      {/* Statistiques */}
      <div className="support-stats">
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-number">{stats.total}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <div className="stat-number">{stats.pending}</div>
              <div className="stat-label">En attente</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">🔄</div>
            <div className="stat-info">
              <div className="stat-number">{stats.processing}</div>
              <div className="stat-label">En traitement</div>
            </div>
          </div>
        </Card>
        <Card className="stat-card" padding="lg">
          <div className="stat-content">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <div className="stat-number">{stats.resolved}</div>
              <div className="stat-label">Résolus</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="filters-card" padding="md">
        <div className="filters-container">
          <div className="filter-group">
            <label>Statut:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="all">Tous</option>
              <option value="pending">⏳ En attente</option>
              <option value="processing">🔄 En traitement</option>
              <option value="resolved">✅ Résolus</option>
              <option value="closed">🔒 Fermés</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="all">Tous</option>
              <option value="help_request">❓ Aide</option>
              <option value="problem_report">🐛 Problème</option>
              <option value="bug">🐞 Bug</option>
              <option value="question">💬 Question</option>
            </select>
          </div>
          {(statusFilter !== 'all' || typeFilter !== 'all') && (
            <Button variant="secondary" size="sm" onClick={() => { setStatusFilter('all'); setTypeFilter('all'); }}>
              🔄 Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* TABLEAU DES DEMANDES */}
      <div className="support-table-container">
        <table className="support-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Type</th>
              <th>Message</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map(request => (
              <tr key={request._id}>
                <td data-label="Utilisateur">
                  <div className="user-cell">
                    <span className="user-avatar-small">👤</span>
                    <span className="user-name">{request.fullName || request.employeeId || 'Utilisateur'}</span>
                  </div>
                </td>
                <td data-label="Email">{request.email || '-'}</td>
                <td data-label="Type">{getTypeBadge(request.type)}</td>
                <td data-label="Message">
                  <div className="message-preview">
                    {(request.message || request.description || '-').substring(0, 50)}
                    {(request.message || request.description || '').length > 50 ? '...' : ''}
                  </div>
                </td>
                <td data-label="Statut">{getStatusBadge(request.status)}</td>
                <td data-label="Date">{formatDate(request.createdAt)}</td>
                <td data-label="Actions">
                  <div className="table-actions">
                    <select
                      value={request.status}
                      onChange={(e) => handleUpdateStatus(request._id, e.target.value)}
                      className="status-select-small"
                    >
                      <option value="pending">⏳ En attente</option>
                      <option value="processing">🔄 En traitement</option>
                      <option value="resolved">✅ Résolu</option>
                      <option value="closed">🔒 Fermé</option>
                    </select>
                    <button 
                      className="action-btn view-btn"
                      onClick={() => setSelectedRequest(request)}
                      title="Voir détails"
                    >
                      👁️
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteRequest(request._id)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredRequests.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">🛟</div>
          <h3>Aucune demande trouvée</h3>
          <p>Aucune demande de support pour le moment</p>
        </Card>
      )}

      {/* Modal Détails */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Détails de la demande</h2>
              <button className="modal-close" onClick={() => setSelectedRequest(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Utilisateur:</label>
                <span>{selectedRequest.fullName || '-'}</span>
              </div>
              <div className="detail-row">
                <label>Email:</label>
                <span>{selectedRequest.email || '-'}</span>
              </div>
              <div className="detail-row">
                <label>ID Employé:</label>
                <span>{selectedRequest.employeeId || selectedRequest.userId || '-'}</span>
              </div>
              <div className="detail-row">
                <label>Type:</label>
                <span>{getTypeBadge(selectedRequest.type)}</span>
              </div>
              <div className="detail-row">
                <label>Statut:</label>
                <span>{getStatusBadge(selectedRequest.status)}</span>
              </div>
              <div className="detail-row">
                <label>Message:</label>
                <div className="detail-message">{selectedRequest.message || selectedRequest.description}</div>
              </div>
              <div className="detail-row">
                <label>Créé le:</label>
                <span>{formatDate(selectedRequest.createdAt)}</span>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="primary" onClick={() => setSelectedRequest(null)}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;